<template>
  <div class="flsa-summary-row-item">
    <button
      type="button"
      class="flsa-summary-row"
      :class="{ 'flsa-summary-row-disabled': disabled }"
      :disabled="disabled"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      <v-icon
        class="flsa-summary-row-chevron flex-shrink-0"
        :class="{ 'flsa-summary-row-chevron-expanded': expanded }"
        icon="mdi-chevron-down"
        size="20"
      />
      <span class="text-body-1 text-textDarkest flsa-summary-row-label">
        {{ label }}
      </span>
      <span
        class="text-body-1 flsa-summary-row-progress"
        :class="progressClass"
      >
        {{ checkedCount }}/{{ totalCount }}
      </span>
    </button>

    <div
      v-if="expanded"
      class="flsa-summary-row-content"
    >
      <FlsaQuestionRow
        v-for="question in questions"
        :key="question.id"
        :question-id="`summary-${sectionId}-${question.id}`"
        :question="question.question"
        :hint="question.hint"
        :citations="question.citations"
        :model-value="answers[`${sectionId}:${question.id}`] ?? false"
        :readonly="readonly"
        @update:model-value="handleQuestionUpdate(question.id, $event)"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import FlsaQuestionRow from './FlsaQuestionRow.vue';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  checkedCount: {
    type: Number,
    required: true,
  },
  totalCount: {
    type: Number,
    required: true,
  },
  questions: {
    type: Array,
    required: true,
  },
  sectionId: {
    type: String,
    required: true,
  },
  answers: {
    type: Object,
    required: true,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:answer']);

const handleQuestionUpdate = (questionId, value) => {
  if (props.readonly) return;

  emit('update:answer', { questionId, value });
};

const expanded = ref(props.checkedCount > 0);

const disabled = computed(() => props.readonly && props.checkedCount === 0);

const progressClass = computed(() => (
  props.checkedCount > 0 ? 'flsa-progress-active' : 'flsa-progress-muted'
));
</script>

<style scoped lang="less">
.flsa-summary-row-item {
  border-bottom: 1px solid @stroke-default;
}

.flsa-summary-row {
  align-items: center;
  background: transparent;
  cursor: pointer;
  display: flex;
  gap: 8px;
  justify-content: flex-start;
  min-height: 57px;
  padding: 0 8px;
  width: 100%;
}

.flsa-summary-row-disabled {
  cursor: default;
  opacity: 0.45;
}

.flsa-summary-row-chevron {
  color: @text-lighter;
  transition: transform 200ms ease;
}

.flsa-summary-row-chevron-expanded {
  transform: rotate(180deg);
}

.flsa-summary-row-label {
  flex: 1 1 auto;
  text-align: left;
}

.flsa-progress-muted {
  color: @text-lighter;
}

.flsa-progress-active {
  color: @text-brand;
}

.flsa-summary-row-content {
  padding: 4px 16px 14px 36px;
}
</style>
